import { calculateCornerCoreFireDoorsPositions } from "./index42.js";
import { calculateFireDoorsPositionsForDistance } from "./index43.js";
import { calculateMuscleCoreFireDoorsPositions } from "./index44.js";
import { positionOutOfCollision } from "./index45.js";
import { DefaultDoor, DefaultWall, GetAllBones, RG_TYPE, RgCreate, roundDecimals } from "@project-rouge/rg-core";
import { applyInverseTransform, applyTransform, distance3d, distanceToLine, getCountOfType, getPivotRaw } from "@project-rouge/rg-three";
import { Vector3 } from "three";
var MIN_DIST_BETWEEN_DOORS = 1.5;
function addFireDoorsToCorridors({ building, dataset, isSprinklered, doorSize = [
	1,
	2,
	.15
], wallId = void 0 }) {
	var _rvData$find;
	const rvData = dataset.rvData;
	const doorId = dataset.modulousIds.corridorFireDoorId;
	const maxDistance = dataset.aggConfig.maxTravelDistBidirectional;
	const deadEndDistance = isSprinklered ? dataset.aggConfig.maxTravelDistSprinklered : dataset.aggConfig.maxTravelDist;
	const realDoorSize = (rvData === null || rvData === void 0 || (_rvData$find = rvData.find((d) => d.modulousId === doorId)) === null || _rvData$find === void 0 ? void 0 : _rvData$find.size) || doorSize;
	for (const bone of GetAllBones(building.data.anatomy)) {
		const tendon = bone.tendon;
		const muscles = bone.muscles;
		const createdRecord = {};
		const boneLevels = building.data.levels.slice(0, bone.floors);
		for (const level of boneLevels) {
			const corridor = building.children.find((c) => c.type === RG_TYPE.Corridor && c.data.tendonID === tendon.id && c.data.level === level.levelIndex);
			const key = getCorridorKey(corridor, building, level.levelIndex);
			if (createdRecord[key]) {
				corridor.children.push(...createdRecord[key].map((w) => RgCreate({ ...w }, rvData)));
				continue;
			}
			createdRecord[key] = [];
			const floor = corridor.children.find((c) => c.type === RG_TYPE.Floor);
			const ceiling = corridor.children.find((c) => c.type === RG_TYPE.Ceiling);
			const wallLength = floor ? floor.size[2] : corridor.size[2];
			const wallBottom = floor ? getPivotRaw([
				.5,
				1,
				.5
			], floor)[1] : 0;
			const wallHeight = ceiling ? getPivotRaw([
				.5,
				0,
				.5
			], ceiling)[1] - wallBottom : corridor.size[1];
			const centerline = [getPivotRaw([
				0,
				0,
				.5
			], corridor), getPivotRaw([
				1,
				0,
				.5
			], corridor)];
			const openingsOnCorridor = getOpeningsOnLine(corridor, [applyInverseTransform(centerline[0], corridor), applyInverseTransform(centerline[1], corridor)]);
			const levelCores = building.children.filter((c) => c.type === RG_TYPE.Core && c.data.level === level.levelIndex);
			const muscleCores = levelCores.filter((c) => muscles.map((m) => m.id).includes(c.data.muscleID));
			muscleCores.sort((a, b) => distanceToBone(b, bone) - distanceToBone(a, bone));
			const targetPositions = [];
			const muscleCoreWallPositions = calculateMuscleCoreFireDoorsPositions(muscleCores, corridor, centerline, deadEndDistance);
			for (let position of muscleCoreWallPositions) {
				position = positionOutOfCollision(position, openingsOnCorridor, realDoorSize);
				targetPositions.push(position);
			}
			const cornerCoreWallPositions = calculateCornerCoreFireDoorsPositions(levelCores.filter((c) => bone.joints.map((j) => j.id).includes(c.data.jointID)), centerline, corridor);
			for (let position of cornerCoreWallPositions) {
				position = positionOutOfCollision(position, openingsOnCorridor, realDoorSize);
				targetPositions.push(position);
			}
			targetPositions.sort((a, b) => {
				const d1 = new Vector3(...a).distanceToSquared(new Vector3(...applyInverseTransform(centerline[0], corridor)));
				const d2 = new Vector3(...b).distanceToSquared(new Vector3(...applyInverseTransform(centerline[0], corridor)));
				return d1 > d2 ? 1 : d1 < d2 ? -1 : 0;
			});
			const splitPositions = calculateFireDoorsPositionsForDistance([
				applyInverseTransform(centerline[0], corridor),
				...targetPositions,
				applyInverseTransform(centerline[1], corridor)
			], maxDistance);
			for (let position of splitPositions) {
				position = positionOutOfCollision(position, openingsOnCorridor, realDoorSize);
				targetPositions.push(position);
			}
			const addedWalls = [];
			for (let i = 0; i < targetPositions.length; i++) {
				const position = targetPositions[i];
				position[1] = wallBottom;
				if (i > 0 && distance3d(position, targetPositions[i - 1]) < MIN_DIST_BETWEEN_DOORS) continue;
				const wall = createWallAndDoor(position, realDoorSize, wallId, doorId, rvData);
				wall.size[0] = wallLength;
				wall.size[1] = wallHeight;
				corridor.children.push(wall);
				addedWalls.push(wall);
			}
			createdRecord[key].push(...addedWalls);
		}
	}
}
function getCorridorKey(corridor, building, level) {
	let key = `${roundDecimals(corridor.size[0])}`;
	key += `|${corridor.children.length}`;
	key += `|${getCountOfType(corridor, RG_TYPE.Opening)}`;
	key += `|${building.children.filter((c) => c.type === RG_TYPE.Apartment && c.data.level === level)}`;
	return key;
}
function distanceToBone(obj, bone) {
	const a = new Vector3(...obj.pos);
	const b = new Vector3(...bone.joints[0].pos);
	return a.distanceToSquared(b);
}
function getOpeningsOnLine(corridor, centerline) {
	const walls = corridor.children.filter((c) => c.type === RG_TYPE.Wall);
	const points = [];
	for (const wall of walls) {
		const openings = wall.children.filter((c) => [
			RG_TYPE.Opening,
			RG_TYPE.Door,
			RG_TYPE.Window
		].includes(c.type));
		for (const opening of openings) points.push([distanceToLine(applyTransform(getPivotRaw([
			0,
			0,
			0
		], opening), wall), centerline).closest.x, distanceToLine(applyTransform(getPivotRaw([
			1,
			0,
			0
		], opening), wall), centerline).closest.x]);
	}
	return points.map((r) => r.sort((a, b) => a > b ? 1 : a < b ? -1 : 0));
}
function createWallAndDoor(wallPos, doorSize, wallId, doorId, rvData) {
	return RgCreate({
		...DefaultWall,
		pos: wallPos,
		anchor: [
			.5,
			0,
			.5
		],
		rot: [
			0,
			90,
			0
		],
		...wallId && { modulousId: wallId },
		children: [RgCreate({
			...DefaultDoor,
			pos: [
				0,
				0,
				0
			],
			rot: [
				0,
				0,
				0
			],
			anchor: [
				.5,
				0,
				.5
			],
			size: doorSize,
			...doorId && { modulousId: doorId }
		}, rvData)]
	}, rvData);
}
export { addFireDoorsToCorridors };

//# sourceMappingURL=index46.js.map